import * as THREE from 'three';

import { SWISS_PROJECTION } from './terrain/swiss-projection.js';

/**
 * Deep-link camera parameters as read from the URL query string.
 *
 * `coordinate` is the WGS84 map center as `"longitude,latitude"` (the same
 * `[lon, lat]` order OpenLayers uses, e.g. from `toLonLat(view.getCenter())`)
 * and `zoom` is the OpenLayers view zoom level (e.g. from `view.getZoom()`).
 */
export interface DeepLinkQueryParams {
  longitude: number;
  latitude: number;
  zoom: number;
}

export interface DeepLinkCameraView {
  /** Camera position in LV95 (EPSG:2056) coordinates, looking straight down. */
  position: THREE.Vector3;
  yaw: number;
  pitch: number;
  /** Distance from the camera to the target on the ground, in meters. */
  radius: number;
}

/** Standard Web Mercator (EPSG:3857) resolution at zoom 0, in meters/pixel, used by OpenLayers, Google Maps, etc. with 256px tiles. */
const WEB_MERCATOR_ZOOM0_RESOLUTION = 156543.03392804097;

/** Assumed average terrain elevation (meters) used to place the look-at target when the real ground height isn't known. */
export const DEFAULT_DEEP_LINK_GROUND_LEVEL = 500;

/**
 * Parse the `coordinate` and `zoom` deep-link query parameters.
 *
 * Returns `null` if either parameter is missing or malformed, so callers can
 * fall back to their normal default view.
 */
export function parseDeepLinkQueryParams(searchParams: URLSearchParams): DeepLinkQueryParams | null {
  const coordinateParam = searchParams.get('coordinate');
  const zoomParam = searchParams.get('zoom');
  if (!coordinateParam || !zoomParam) {
    return null;
  }

  const parts = coordinateParam.split(',').map(part => Number(part.trim()));
  if (parts.length !== 2 || parts.some(value => !Number.isFinite(value))) {
    return null;
  }

  const zoom = Number(zoomParam);
  if (!Number.isFinite(zoom)) {
    return null;
  }

  const [longitude, latitude] = parts;
  return { longitude, latitude, zoom };
}

/**
 * Convert an OpenLayers-style WGS84 coordinate + zoom into a top-down
 * (vertical) camera view for the Potree scene, expressed in LV95 coordinates.
 *
 * The camera altitude is derived so that the vertical extent of ground
 * visible through the given field of view roughly matches what the OpenLayers
 * map showed at that zoom level for the given viewport height, keeping the
 * two apps visually in sync when deep-linking between them.
 */
export function computeDeepLinkCameraView(
  params: DeepLinkQueryParams,
  options: {
    viewportHeightPx: number;
    verticalFovDegrees: number;
    groundLevel?: number;
  },
): DeepLinkCameraView {
  const { viewportHeightPx, verticalFovDegrees, groundLevel = DEFAULT_DEEP_LINK_GROUND_LEVEL } =
    options;

  const [x, y] = SWISS_PROJECTION.wgs84ToLv95([params.longitude, params.latitude]);

  const metersPerPixel = WEB_MERCATOR_ZOOM0_RESOLUTION / Math.pow(2, params.zoom);
  const groundExtentMeters = metersPerPixel * viewportHeightPx;
  const verticalFovRad = (verticalFovDegrees * Math.PI) / 180;
  const radius = groundExtentMeters / (2 * Math.tan(verticalFovRad / 2));

  return {
    position: new THREE.Vector3(x, y, groundLevel + radius),
    yaw: 0,
    pitch: -Math.PI / 2,
    radius,
  };
}
